import {
  DataType,
  type SettingTypeMetadata,
  type SettingTypeValueMap,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createSettingsParam,
  createSettingsParams,
  type ISettingsParam,
  SettingsParamType,
} from '@wix/tpa-settings';
import { AccessibilityHtmlTags } from '../../utils/AccessibilityHtmlTags.const';
import {
  AlignmentOptions,
  GalleryArrowPositionOptions,
  GalleryItemSizeOptions,
  GalleryLayoutOptions,
  ImagePositionOptions,
  ImageResizeOptions,
  type ISection,
  SectionTypes,
  SidebarPosition,
} from './types';
import { SERVICE_PAGE_SLOT_ID } from './constants';

export const defaultSections: ISection[] = [
  {
    type: SectionTypes.TITLE_TAGLINE,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.DETAILS,
    visible: true,
    bookButton: true,
  },
  {
    type: SectionTypes.DESCRIPTION,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.GALLERY,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.SCHEDULING,
    visible: true,
    bookButton: true,
  },
  {
    type: SectionTypes.POLICY,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.CONTACT,
    visible: true,
    bookButton: false,
  },
  {
    type: SectionTypes.PLUGIN,
    visible: true,
    bookButton: false,
  },
];

interface DesktopSettingsKeys {
  policySectionTitle: SettingsParamType.Text;
  sections: ISection[];
  headerVisibility: SettingsParamType.Boolean;
  headerTitleVisibility: SettingsParamType.Boolean;
  headerTitleAlignment: AlignmentOptions;
  headerImageVisibility: SettingsParamType.Boolean;
  headerImagePosition: ImagePositionOptions;
  headerBookButtonVisibility: SettingsParamType.Boolean;
  sidebarVisibility: SettingsParamType.Boolean;
  onlineBadgeVisibility: SettingsParamType.Boolean;
  onlineBadgeText: SettingsParamType.Text;
  serviceTitleVisibility: SettingsParamType.Boolean;
  serviceTaglineVisibility: SettingsParamType.Boolean;
  detailsDurationVisibility: SettingsParamType.Boolean;
  detailsPriceVisibility: SettingsParamType.Boolean;
  detailsLocationVisibility: SettingsParamType.Boolean;
  detailsBoxAlignment: AlignmentOptions | undefined;
  detailsTextAlignment: AlignmentOptions | undefined;
  detailsButtonAlignment: AlignmentOptions | undefined;
  titleAndTaglineAlignment: AlignmentOptions | undefined;
  businessEmailVisibility: SettingsParamType.Boolean;
  businessPhoneNumberVisibility: SettingsParamType.Boolean;
  businessAddressVisibility: SettingsParamType.Boolean;
  locationNameVisibility: SettingsParamType.Boolean;
  contactDetailsTitleText: SettingsParamType.Text;
  descriptionTitleText: SettingsParamType.Text;
  bookButtonText: SettingsParamType.Text;
  pendingApprovalButtonText: SettingsParamType.Text;
  serviceUnavailableMessageText: SettingsParamType.Text;
  availabilityMessageText: SettingsParamType.Text;
  numberOfSpotsLeftMessageText: SettingsParamType.Text;
  fullCourseMessageText: SettingsParamType.Text;
  unBookableCourseMessageText: SettingsParamType.Text;
  bookStartedCourseMessageText: SettingsParamType.Text;
  sidebarSection: SectionTypes;
  sidebarFreezePosition: SettingsParamType.Boolean;
  sidebarPosition: SidebarPosition;
  sidebarAlignment: AlignmentOptions;
  bodyAlignment: AlignmentOptions;
  columnPosition: AlignmentOptions;
  scheduleDays: SettingsParamType.Number;
  scheduleSectionTitle: SettingsParamType.Text;
  scheduleLoadAllText: SettingsParamType.Text;
  scheduleEmptyStateText: SettingsParamType.Text;
  scheduleDurationVisibility: SettingsParamType.Boolean;
  scheduleStaffVisibility: SettingsParamType.Boolean;
  showSessionLimit: SettingsParamType.Boolean;
  scheduleSessionLimit: SettingsParamType.Number;
  headerTitleHtmlTag: SettingsParamType.String;
  bodyServiceTitleHtmlTag: SettingsParamType.String;
  bodySectionTitleHtmlTag: SettingsParamType.String;
  sidebarServiceTitleHtmlTag: SettingsParamType.String;
  sidebarTitleHtmlTag: SettingsParamType.String;
  galleryLayout: GalleryLayoutOptions;
  galleryMasonryItemSpacing: SettingsParamType.Number;
  galleryMasonryItemBorderWidth: SettingsParamType.Number;
  galleryMasonryItemRadius: SettingsParamType.Number;
  galleryMasonryItemSize: GalleryItemSizeOptions;
  galleryGridItemSpacing: SettingsParamType.Number;
  galleryGridItemBorderWidth: SettingsParamType.Number;
  galleryGridItemRadius: SettingsParamType.Number;
  galleryGridThumbnailSize: SettingsParamType.Number;
  gallerySliderItemSpacing: SettingsParamType.Number;
  gallerySliderItemBorderWidth: SettingsParamType.Number;
  gallerySliderItemRadius: SettingsParamType.Number;
  gallerySliderImageResizeOption: ImageResizeOptions;
  gallerySliderLoopImages: SettingsParamType.Boolean;
  gallerySliderAutoSlide: SettingsParamType.Boolean;
  gallerySliderTimeBetweenImages: SettingsParamType.Number;
  gallerySliderShowArrows: SettingsParamType.Boolean;
  gallerySliderArrowsSize: SettingsParamType.Number;
  gallerySliderArrowsPosition: GalleryArrowPositionOptions;
  gallerySliderHeight: SettingsParamType.Number;
  buttonsFullWidth: SettingsParamType.Boolean;
  courseAvailability: SettingsParamType.Boolean;
  displayNumberOfSpots: SettingsParamType.Boolean;
  servicePageSlot: SettingsParamType.Object;
}

interface MobileSettingsKeys {
  mobileSections: SectionTypes[] | undefined;
}

export type ISettingsParams = DesktopSettingsKeys & MobileSettingsKeys;

export const uninitializedVisibilitySettingsFalseValue = -1 as any as boolean;
export const uninitializedVisibilitySettingsTrueValue = -2 as any as boolean;

const getMetadata = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
): SettingTypeMetadata => ({
  type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

const settingsParam = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(type, defaultValue),
});

const enumSettingsParam = <T extends string>(defaultValue: T) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(DataType.textEnum, defaultValue),
});

const settingsParamWithUndefinedDefault = <T>() => ({
  getDefaultValue: () => undefined as unknown as T,
  metadata: getMetadata(DataType.textEnum, undefined as unknown as string),
});

const typedSettingsParam = <T extends DataType, Type extends SettingsParamType>(
  dataType: T,
  type: Type | undefined,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  type,
  ...settingsParam(dataType, defaultValue),
});

const booleanSettingsParam = (defaultValue: boolean) =>
  typedSettingsParam(
    DataType.booleanValue,
    SettingsParamType.Boolean,
    defaultValue,
  );

const settingsParamWithoutInheritedDesktop = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  inheritDesktop: false,
  ...settingsParam(type, defaultValue),
});

const settingsParamWithMobileDefault = <
  T extends DataType,
  VType extends SettingTypeValueMap[T]['value'],
>(
  type: T,
  mobileDefaultValue: VType,
  defaultValue: VType,
) => ({
  inheritDesktop: false,
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  metadata: {
    type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
    defaultValue: {
      type: 'static' as const,
      value: defaultValue,
    },
    mobileDefaultValue: {
      type: 'static' as const,
      value: mobileDefaultValue,
    },
  },
});

const enumSettingsParamWithRTLDefault = <T extends string>(
  rtlDefaultValue: T,
  ltrDefaultValue: T,
): Omit<ISettingsParam, 'key'> => ({
  getDefaultValue: ({ isRTL }) => (isRTL ? rtlDefaultValue : ltrDefaultValue),
  metadata: getMetadata(DataType.textEnum, ltrDefaultValue), // TODO: Fix metadata
});

const textSettingsParam = (
  defaultValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

const scheduleDays = createSettingsParam(
  'scheduleDays',
  typedSettingsParam(DataType.number, SettingsParamType.Number, 7),
) as ISettingsParam;

const servicePageSlot = createSettingsParam('servicePageSlot', {
  type: SettingsParamType.Object,
  getDefaultValue: () => null as any,
  metadata: {
    type: DataType.container,
    dataItemManifestData: {
      container: {
        containerType: 'slot',
        slot: {
          slotId: SERVICE_PAGE_SLOT_ID,
        },
      },
    },

    defaultValue: {
      type: 'static',
      value: {},
    },
  },
});

export default createSettingsParams<ISettingsParams>({
  sections: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    defaultSections,
  ),
  headerVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  headerTitleVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsFalseValue,
  ),
  headerTitleAlignment: enumSettingsParam(AlignmentOptions.CENTER),
  headerImageVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  headerImagePosition: enumSettingsParam(ImagePositionOptions.MIDDLE),
  headerBookButtonVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsFalseValue,
  ),
  sidebarVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsFalseValue,
  ),
  sidebarSection: enumSettingsParam(SectionTypes.CONTACT),
  sidebarFreezePosition: booleanSettingsParam(false),
  sidebarPosition: enumSettingsParamWithRTLDefault(
    SidebarPosition.LEFT,
    SidebarPosition.RIGHT,
  ),
  sidebarAlignment: enumSettingsParamWithRTLDefault(
    AlignmentOptions.RIGHT,
    AlignmentOptions.LEFT,
  ),
  onlineBadgeVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  serviceTitleVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  serviceTaglineVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  titleAndTaglineAlignment:
    settingsParamWithUndefinedDefault<AlignmentOptions>(),
  policySectionTitle: textSettingsParam(
    'app.settings.sections.policy.default.title',
  ),
  detailsDurationVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  detailsPriceVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  detailsLocationVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  detailsBoxAlignment: settingsParamWithUndefinedDefault<AlignmentOptions>(),
  detailsTextAlignment: settingsParamWithUndefinedDefault<AlignmentOptions>(),
  detailsButtonAlignment: settingsParamWithUndefinedDefault<AlignmentOptions>(),
  businessEmailVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  businessPhoneNumberVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  businessAddressVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  locationNameVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsFalseValue,
  ),
  contactDetailsTitleText: textSettingsParam(
    'app.settings.sections.contact.default.contact-details-title.label',
  ),
  descriptionTitleText: textSettingsParam('app.description.title'),
  onlineBadgeText: textSettingsParam(
    'app.settings.sections.titles.default.online-badge.text',
  ),
  bookButtonText: textSettingsParam('app.widget.cta.book-now'),
  pendingApprovalButtonText: textSettingsParam(
    'app.widget.cta.pending-approval',
  ),
  serviceUnavailableMessageText: textSettingsParam(
    'app.widget.uou-messages.book-not-available',
  ),
  availabilityMessageText: textSettingsParam('service.course.available-spots'),
  numberOfSpotsLeftMessageText: typedSettingsParam(
    DataType.text,
    SettingsParamType.Text,
    '',
  ),
  fullCourseMessageText: textSettingsParam(
    'app.widget.uou-messages.fully-booked',
  ),
  unBookableCourseMessageText: textSettingsParam(
    'app.widget.uou-messages.book-already-closed',
  ),
  bookStartedCourseMessageText: textSettingsParam(
    'app.widget.uou-messages.book-available-when-service-started',
  ),
  bodyAlignment: enumSettingsParamWithRTLDefault(
    AlignmentOptions.RIGHT,
    AlignmentOptions.LEFT,
  ),
  columnPosition: enumSettingsParam(AlignmentOptions.CENTER),
  scheduleDays,
  scheduleSectionTitle: textSettingsParam('app.scheduling.title.default'),
  scheduleLoadAllText: textSettingsParam(
    'app.scheduling.load-all-sessions.default',
  ),
  scheduleEmptyStateText: {
    getDefaultValue: ({ t, getSettingParamValue }) =>
      t('app.scheduling.empty-state', {
        daysAhead: getSettingParamValue(scheduleDays),
      }),
    type: SettingsParamType.Text,
  },
  scheduleDurationVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  scheduleStaffVisibility: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  showSessionLimit: booleanSettingsParam(true),
  scheduleSessionLimit: settingsParam(DataType.number, 3),
  headerTitleHtmlTag: enumSettingsParam(AccessibilityHtmlTags.MainHeader),
  sidebarServiceTitleHtmlTag: enumSettingsParam(
    AccessibilityHtmlTags.MainHeader,
  ),
  bodyServiceTitleHtmlTag: enumSettingsParam(AccessibilityHtmlTags.MainHeader),
  bodySectionTitleHtmlTag: enumSettingsParam(
    AccessibilityHtmlTags.SecondaryHeader,
  ),
  sidebarTitleHtmlTag: enumSettingsParam(AccessibilityHtmlTags.SecondaryHeader),
  galleryLayout: enumSettingsParam(GalleryLayoutOptions.GRID),
  galleryMasonryItemSize: enumSettingsParam(GalleryItemSizeOptions.MEDIUM),
  galleryMasonryItemSpacing: settingsParamWithoutInheritedDesktop(
    DataType.number,
    12,
  ),
  galleryMasonryItemBorderWidth: settingsParam(DataType.number, 0),
  galleryMasonryItemRadius: settingsParam(DataType.number, 0),
  galleryGridThumbnailSize: settingsParamWithMobileDefault(
    DataType.number,
    200,
    280,
  ),
  galleryGridItemSpacing: settingsParamWithoutInheritedDesktop(
    DataType.number,
    12,
  ),
  galleryGridItemBorderWidth: settingsParam(DataType.number, 0),
  galleryGridItemRadius: settingsParam(DataType.number, 0),
  gallerySliderItemSpacing: settingsParamWithoutInheritedDesktop(
    DataType.number,
    12,
  ),
  gallerySliderItemBorderWidth: settingsParam(DataType.number, 0),
  gallerySliderItemRadius: settingsParam(DataType.number, 0),
  gallerySliderImageResizeOption: enumSettingsParam(ImageResizeOptions.FIT),
  gallerySliderTimeBetweenImages: settingsParam(DataType.number, 4),
  gallerySliderLoopImages: booleanSettingsParam(true),
  gallerySliderAutoSlide: booleanSettingsParam(false),
  gallerySliderShowArrows: booleanSettingsParam(true),
  gallerySliderArrowsSize: settingsParamWithMobileDefault(
    DataType.number,
    18,
    16,
  ),
  gallerySliderArrowsPosition: settingsParamWithMobileDefault(
    DataType.textEnum,
    GalleryArrowPositionOptions.INSIDE,
    GalleryArrowPositionOptions.OUTSIDE,
  ),
  gallerySliderHeight: settingsParamWithMobileDefault(
    DataType.number,
    200,
    400,
  ),
  buttonsFullWidth: settingsParamWithoutInheritedDesktop(
    DataType.booleanValue,
    false,
  ),
  mobileSections: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    undefined,
  ),
  courseAvailability: booleanSettingsParam(
    uninitializedVisibilitySettingsTrueValue,
  ),
  displayNumberOfSpots: booleanSettingsParam(false),
  servicePageSlot,
});
